/**
 * CallAskPage — /calls/:callSlug/ask/:kind (kind = seat | question | idea)
 *
 * Response page for the free-text Call Asks (Hot Seat, questions, ideas —
 * BN#2935/PR#1470). Reached from the corresponding button in a call reminder
 * email. GET resolves the ask's own wording + this person's current state
 * (already submitted? closed? full?) from their signed token; POST submits.
 * See worker/src/calls-asks.ts for the response rules.
 */

import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

type AskKind = 'seat' | 'question' | 'idea';

interface AskData {
  firstName: string | null;
  prompt: string;
  ctaLabel: string;
  helperText: string | null;
  closed: boolean;
  full: boolean;
  existingText: string | null;
}

type PageState =
  | { phase: 'loading' }
  | { phase: 'ready'; data: AskData }
  | { phase: 'sent'; data: AskData }
  | { phase: 'error'; message: string };

const KIND_LABEL: Record<AskKind, string> = { seat: 'the Hot Seat', question: 'a question', idea: 'an idea' };

export default function CallAskPage() {
  const { callSlug, kind } = useParams<{ callSlug: string; kind: string }>();
  const [searchParams] = useSearchParams();
  const t = searchParams.get('t') ?? '';
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validKind: AskKind | null = kind === 'seat' || kind === 'question' || kind === 'idea' ? kind : null;

  useEffect(() => {
    if (!callSlug || !validKind || !t) {
      setState({ phase: 'error', message: 'This link is incomplete.' });
      return;
    }
    fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/ask/${validKind}?t=${encodeURIComponent(t)}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({})) as { ok?: boolean; error?: string } & Partial<AskData>;
        if (!r.ok || !d.ok) {
          setState({ phase: 'error', message: d.error || 'This link has expired.' });
          return;
        }
        const data: AskData = {
          firstName: d.firstName ?? null,
          prompt: d.prompt ?? '',
          ctaLabel: d.ctaLabel ?? 'Send',
          helperText: d.helperText ?? null,
          closed: !!d.closed,
          full: !!d.full,
          existingText: d.existingText ?? null,
        };
        setText(data.existingText ?? '');
        setState({ phase: data.existingText ? 'sent' : 'ready', data });
      })
      .catch(() => setState({ phase: 'error', message: 'Something went wrong — please try the link again.' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callSlug, validKind, t]);

  const submit = () => {
    if (!callSlug || !validKind || !text.trim()) return;
    setSubmitting(true);
    setSubmitError(null);
    fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/ask/${validKind}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ t, text }),
    })
      .then(async (r) => {
        const d = await r.json().catch(() => ({})) as { ok?: boolean; error?: string };
        if (!r.ok || !d.ok) { setSubmitError(d.error || "Couldn't send that — please try again."); return; }
        setState((prev) => prev.phase === 'ready' ? { phase: 'sent', data: prev.data } : prev);
      })
      .catch(() => setSubmitError("Couldn't send that — please try again."))
      .finally(() => setSubmitting(false));
  };

  const callPagePath = callSlug ? `/calls/${encodeURIComponent(callSlug)}` : '/';

  return (
    <TakerShell>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '56px 24px 80px', fontFamily: "'Poppins', sans-serif" }}>
        {state.phase === 'loading' && (
          <div style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, padding: '40px 0' }}>One moment…</div>
        )}

        {state.phase === 'error' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#991b1b', fontSize: 15, marginBottom: 18 }}>{state.message}</p>
            <Link to={callPagePath} style={{ color: '#F05A28', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
              ← Back to the call page
            </Link>
          </div>
        )}

        {(state.phase === 'ready' || state.phase === 'sent') && (
          <>
            <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 24, fontWeight: 800, color: '#0A0A2E', marginBottom: 12, lineHeight: 1.3 }}>
              {state.data.prompt}
            </h1>
            {state.data.helperText && (
              <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 22 }}>{state.data.helperText}</p>
            )}

            {state.phase === 'sent' ? (
              <div style={{ background: '#FFF5F1', border: '1px solid #FFD9CC', borderRadius: 10, padding: '20px 22px' }}>
                <p style={{ fontSize: 15, color: '#0A0A2E', fontWeight: 600, marginBottom: 6 }}>
                  {state.data.firstName ? `Thanks, ${state.data.firstName} — sent.` : 'Thanks — sent.'}
                </p>
                <p style={{ fontSize: 14, color: '#4b5563', lineHeight: 1.6 }}>
                  {validKind === 'seat'
                    ? "We've got your problem — Lisa will pick from the queue on the day."
                    : "It's on the list for the call."}
                </p>
                {validKind === 'seat' && !state.data.closed && (
                  <button
                    type="button"
                    onClick={() => setState({ phase: 'ready', data: state.data })}
                    style={{ marginTop: 12, background: 'none', border: 'none', color: '#F05A28', fontWeight: 600, fontSize: 13, cursor: 'pointer', padding: 0 }}
                  >
                    Update what you wrote
                  </button>
                )}
              </div>
            ) : state.data.closed ? (
              <p style={{ color: '#6b7280', fontSize: 14 }}>This one's closed for the upcoming call.</p>
            ) : state.data.full ? (
              <div>
                <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 12 }}>The seats are full for this call.</p>
                <Link to={`/calls/${encodeURIComponent(callSlug ?? '')}/ask/question?t=${encodeURIComponent(t)}`} style={{ color: '#F05A28', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
                  Got a question for Lisa instead? →
                </Link>
              </div>
            ) : (
              <>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  maxLength={500}
                  rows={5}
                  placeholder={`Tell us a little about ${KIND_LABEL[validKind ?? 'question']}…`}
                  style={{
                    width: '100%', boxSizing: 'border-box', padding: '14px 16px', fontSize: 15,
                    fontFamily: "'Poppins', sans-serif", border: '1px solid #d1d5db', borderRadius: 8,
                    resize: 'vertical' as const, marginBottom: 14,
                  }}
                />
                {submitError && <p style={{ color: '#991b1b', fontSize: 13, marginBottom: 10 }}>{submitError}</p>}
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting || !text.trim()}
                  style={{
                    background: submitting || !text.trim() ? '#f3a37e' : '#F05A28', color: '#fff', fontWeight: 700,
                    fontSize: 14, padding: '13px 26px', borderRadius: 8, border: 'none',
                    cursor: submitting || !text.trim() ? 'default' : 'pointer',
                  }}
                >
                  {submitting ? 'Sending…' : state.data.ctaLabel}
                </button>
              </>
            )}

            <div style={{ marginTop: 28 }}>
              <Link to={callPagePath} style={{ color: '#9ca3af', fontWeight: 500, fontSize: 13, textDecoration: 'none' }}>
                ← Back to the call page
              </Link>
            </div>
          </>
        )}
      </div>
    </TakerShell>
  );
}
